/**
 * How much the workspace toolbar must give up to stay on one line.
 *
 * The toolbar is the view tabs on the left and, on the right, the switcher
 * (while expanded), the storage strip and the focus-mode controls. What fits
 * depends on those pieces — a long workspace name, whether the page is
 * expanded — not only on the window, so this MEASURES rather than using
 * breakpoints. Things give way in order:
 *
 *   0  everything in full
 *   1  the storage strip drops its figures ("1.7 GB of 5.0 GB") to an icon —
 *      Browse, which has the room, shows the figures instead
 *   2  the focus-mode controls drop their text labels to icons
 *
 * and past level 2 the row wraps.
 *
 * Each piece's width is remembered in each form once it has been shown, so
 * the decision to bring something back uses the same number that took it away
 * (no flicker at a boundary). A form not yet seen is estimated at its current
 * width and corrected once shown; the measurement runs in a layout effect on
 * every level change, so any correction lands before the browser paints.
 */
import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';

export type ToolbarRoomLevel = 0 | 1 | 2;

export interface ToolbarRoomRefs {
  /** The whole toolbar row. */
  row: RefObject<HTMLElement | null>;
  /** The piece that never gives way (the view tabs). */
  fixed: RefObject<HTMLElement | null>;
  /** The group on the right; its children are summed as one line. */
  end: RefObject<HTMLElement | null>;
  /** The storage strip's wrapper — a child of `end`. */
  strip: RefObject<HTMLElement | null>;
  /** The focus-mode controls' wrapper — a child of `end`, when present. */
  controls: RefObject<HTMLElement | null>;
}

interface SeenWidths {
  stripFull: number;
  stripIcon: number;
  controlsFull: number;
  controlsIcons: number;
}

const LEVELS: ToolbarRoomLevel[] = [0, 1, 2];

function innerWidth(el: HTMLElement): number {
  const style = getComputedStyle(el);
  return (
    el.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0)
  );
}

function columnGap(el: HTMLElement): number {
  return parseFloat(getComputedStyle(el).columnGap) || 0;
}

export function useToolbarRoom(refs: ToolbarRoomRefs): ToolbarRoomLevel {
  const [level, setLevel] = useState<ToolbarRoomLevel>(0);
  const levelRef = useRef<ToolbarRoomLevel>(0);
  const seen = useRef<SeenWidths>({
    stripFull: 0,
    stripIcon: 0,
    controlsFull: 0,
    controlsIcons: 0,
  });

  const measure = useCallback(() => {
    const row = refs.row.current;
    const fixed = refs.fixed.current;
    const end = refs.end.current;
    if (!row || !fixed || !end) return;
    const current = levelRef.current;
    const strip = refs.strip.current?.offsetWidth ?? 0;
    const controls = refs.controls.current?.offsetWidth ?? 0;
    const s = seen.current;
    if (current === 0) s.stripFull = strip;
    else s.stripIcon = strip;
    if (current < 2) s.controlsFull = controls;
    else s.controlsIcons = controls;

    // The end group may already be wrapping inside itself; its children's
    // widths are what it needs on one line.
    const kids = Array.from(end.children) as HTMLElement[];
    const kidsWidth = kids.reduce((sum, kid) => sum + kid.offsetWidth, 0);
    const gaps = columnGap(row) + columnGap(end) * Math.max(0, kids.length - 1);
    const base = fixed.offsetWidth + gaps + kidsWidth - strip - controls;
    const needed = (l: ToolbarRoomLevel) =>
      base +
      (l === 0 ? s.stripFull || strip : s.stripIcon || strip) +
      (l < 2 ? s.controlsFull || controls : s.controlsIcons || controls);

    const room = innerWidth(row);
    const next = LEVELS.find((l) => needed(l) <= room) ?? 2;
    if (next !== current) {
      levelRef.current = next;
      setLevel(next);
    }
  }, [refs.row, refs.fixed, refs.end, refs.strip, refs.controls]);

  // Re-measure after each level change, before paint: a form shown for the
  // first time corrects its estimate here.
  useLayoutEffect(measure, [measure, level]);

  useLayoutEffect(() => {
    const row = refs.row.current;
    const fixed = refs.fixed.current;
    const end = refs.end.current;
    if (!row || !fixed || !end || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(fixed);
    observer.observe(end);
    return () => observer.disconnect();
  }, [refs.row, refs.fixed, refs.end, measure]);

  return level;
}
